import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button, Column, Row, TableCell, TableRow, Typography,
} from '@tempmee/react';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';
import { PromoCodeStatusLabel, promoCodePath } from '../../PromoCodes';

// The ticket's columns. "Discount Name" and "Code Name" lead, matching the
// General Promo Codes list's Name/Code pair (GOTU-7716) — the discount's
// descriptor, then the code an office types to redeem it. "Max Uses" is the
// per-office cap (0 when unlimited); the campaign-wide cap lives on the code's
// own page behind the Action link.
//
// Heads up on "Code Name": it is GOTU-7716's wording, and it is the opposite of
// what "code name" means elsewhere in this feature — PromoCodeBreadcrumbs takes
// `codeName={promoCode.name}` and promoCodes.js searches `name` under that
// label, both meaning the campaign's name. The General list calls this same
// value "Code". Raised with the ticket author; the labels here follow the
// ticket until they say otherwise.
const COLUMNS = [
  'Discount Name',
  'Code Name',
  'Discount',
  'Start Date',
  'End Date',
  'Code Status',
  'Applied',
  '# Used',
  'Max Uses',
  'Action',
];

// A node, not a string, for the reason BestInDentalCycleHistory gives:
// TableCard's default string title is 18px bold, which reads too heavy inside
// the profile tab.
const CARD_TITLE = (
  <Typography variant="subtitle2" as="h2" bold className="truncate text-text-primary">
    Discounts &amp; Promo Codes
  </Typography>
);

const Caption = ({ children }) => (
  <Typography variant="caption" className="text-text-secondary">
    {children}
  </Typography>
);

Caption.propTypes = { children: PropTypes.node.isRequired };

// "Yes" alone does not say whether the office entered the code or an admin did
// it for them (GOTU-9821), and a removed code has to say who took it back and
// when. A row with no applier to name — an automatic grant with no opt-in, or
// "No" — shows the label alone.
const AppliedCell = ({ row }) => {
  let detail = null;
  if (row.revoked) {
    detail = row.revokedByName
      ? `by ${row.revokedByName} on ${row.revokedAtLabel}`
      : `on ${row.revokedAtLabel}`;
  } else if (row.applied && row.appliedByName) {
    detail = `by ${row.appliedByName}`;
  }

  return (
    <Column>
      <Typography variant="body2">{row.appliedLabel}</Typography>
      {detail ? <Caption>{detail}</Caption> : null}
    </Column>
  );
};

AppliedCell.propTypes = {
  row: PropTypes.shape({
    applied: PropTypes.bool.isRequired,
    appliedLabel: PropTypes.string.isRequired,
    appliedByName: PropTypes.string.isRequired,
    revoked: PropTypes.bool.isRequired,
    revokedByName: PropTypes.string.isRequired,
    revokedAtLabel: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * One office's promo history (GOTU-6663): every code it holds or has redeemed
 * against, newest activity first, with whether and how often it was used. Uses
 * the shared `TableCardFrame` — `outline` so the card stands out from
 * the tab's white background.
 *
 * "Applied" is the office-level flag the ticket asks for, and "No" is a row you
 * will actually see: campaign-service #123 (GOTU-5660) added the live campaigns
 * an office matches but has never entered or been swept up by.
 *
 * `action` is the card's header control (the tab's "Apply promo code"), and
 * `onRemove(row)` is called from a row's Remove button, which only an
 * application that can still be taken back gets (GOTU-9821).
 */
export const OfficePromoCodesTable = ({
  history, isPending, isError, action, onRemove,
}) => (
  <TableCardFrame
    title={CARD_TITLE}
    outline
    action={action}
    columns={COLUMNS}
    rowCount={history.length}
    isPending={isPending}
    isError={isError}
    emptyMessage="This office has not been on any promotion yet."
    errorMessage="Could not load discounts and promo codes."
  >
    {history.map((row) => (
      <TableRow key={row.campaignId} hover>
        <TableCell>{row.campaignName}</TableCell>
        {/* Codes have no spaces, so a long one would otherwise widen the table
            until the right-hand columns scroll off — the same reason
            PromoCodesTable breaks this cell. QA has a 60-character code on dev. */}
        <TableCell className="break-all">{row.codeLabel}</TableCell>
        <TableCell>{row.discountLabel}</TableCell>
        <TableCell>{row.startDateLabel}</TableCell>
        <TableCell>{row.endDateLabel}</TableCell>
        <TableCell>
          <PromoCodeStatusLabel status={row.status} label={row.statusLabel} />
        </TableCell>
        <TableCell>
          <AppliedCell row={row} />
        </TableCell>
        <TableCell>{row.usedCountLabel}</TableCell>
        <TableCell>{row.maxUsesLabel}</TableCell>
        <TableCell>
          <Row gap={2} align="center">
            <RouterLink to={promoCodePath(row.campaignId)}>View Code</RouterLink>
            {row.removable ? (
              <Button variant="text" color="error" size="small" onClick={() => onRemove(row)}>
                Remove
              </Button>
            ) : null}
          </Row>
        </TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

OfficePromoCodesTable.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      campaignId: PropTypes.string.isRequired,
      campaignName: PropTypes.string.isRequired,
      codeLabel: PropTypes.string.isRequired,
      discountLabel: PropTypes.string.isRequired,
      startDateLabel: PropTypes.string.isRequired,
      endDateLabel: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      applied: PropTypes.bool.isRequired,
      appliedLabel: PropTypes.string.isRequired,
      appliedByName: PropTypes.string.isRequired,
      revoked: PropTypes.bool.isRequired,
      revokedByName: PropTypes.string.isRequired,
      revokedAtLabel: PropTypes.string.isRequired,
      removable: PropTypes.bool.isRequired,
      usedCountLabel: PropTypes.string.isRequired,
      maxUsesLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  action: PropTypes.node,
  onRemove: PropTypes.func.isRequired,
};

OfficePromoCodesTable.defaultProps = {
  action: null,
};

export default OfficePromoCodesTable;
